import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import { getSignalIdsFromGroupedSignals } from "app/pages/sales-intelligence/domain/signals/utils/signal-ids";
import { resetFilteringStateAction } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/action-creators";
import { setLeadsFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters/store/action-creators";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import signalsService from "../services/signalsService";

import * as ac from "./action-creators";
import initialSignalsState from "./initial-state";
import type { SignalsState } from "./types";

const signalsReducer = createReducer<
    SignalsState,
    ActionType<typeof ac> | ReturnType<typeof resetFilteringStateAction> | ReturnType<typeof setLeadsFilterValue>
>(initialSignalsState)
    .handleAction(
        ac.appliedSignalsChangedAction,
        (state, { payload }): SignalsState => ({
            ...state,
            appliedSignalsIds: payload,
        }),
    )
    /**
     * Cross-slice load: a saved search dispatches its filters here with
     * `signalsFilters` populated. The interactive edit path strips signals out
     * (so its payload omits `signalsFilters`), hence the presence guard.
     */
    .handleAction(
        setLeadsFilterValue,
        (state, { payload }): SignalsState =>
            is.undefined(payload.signalsFilters)
                ? state
                : { ...state, appliedSignalsIds: getSignalIdsFromGroupedSignals(payload.signalsFilters) },
    )
    .handleAction(ac.addAppliedSignalsAction, (state, { payload }): SignalsState => {
        const newSignalsIds = signalsService.appendUniqueSignalsToAppliedSignals(
            state.appliedSignalsIds,
            payload,
            state.signalsById,
        );

        return {
            ...state,
            appliedSignalsIds: newSignalsIds,
        };
    })
    .handleAction(
        ac.setShouldAutoApplyCreatedSignal,
        (state, { payload }): SignalsState => ({
            ...state,
            shouldAutoApplyCreatedSignal: payload,
        }),
    )
    .handleAction(
        ac.createdSignalAutoAppliedAction,
        (state, { payload }): SignalsState => ({
            ...state,
            lastAutoAppliedCreatedSignal: payload,
        }),
    )
    .handleAction(
        ac.clearLastAutoAppliedCreatedSignalAction,
        (state): SignalsState => ({
            ...state,
            lastAutoAppliedCreatedSignal: null,
        }),
    )
    .handleAction(ac.removeAppliedSignalsAction, (state, { payload }): SignalsState => {
        const newSignalsIds = state.appliedSignalsIds.filter((id) => !payload.includes(id));

        return {
            ...state,
            appliedSignalsIds: newSignalsIds,
        };
    })
    .handleAction(
        ac.isDropdownOpenAction,
        (state, { payload }): SignalsState => ({
            ...state,
            isDropdownOpen: payload,
        }),
    )
    .handleAction(
        ac.isSignalsManagementOpenAction,
        (state, { payload }): SignalsState => ({
            ...state,
            isSignalsManagementOpen: payload,
            shouldAutoApplyCreatedSignal: payload ? false : state.shouldAutoApplyCreatedSignal,
        }),
    )
    .handleAction(
        ac.setPreselectedSignalType,
        (state, { payload }): SignalsState =>
            payload === null
                ? {
                      ...state,
                      preselectedSignalType: null,
                      shouldAutoApplyCreatedSignal: false,
                  }
                : {
                      ...state,
                      preselectedSignalType: payload,
                      managementEditSignalId: null,
                      managementDeleteSignalId: null,
                  },
    )
    .handleAction(
        ac.openSignalsManagementForType,
        (state, { payload }): SignalsState => ({
            ...state,
            isSignalsManagementOpen: true,
            preselectedSignalType: payload,
            initialSelectedSignalType: payload,
            shouldAutoApplyCreatedSignal: false,
            managementEditSignalId: null,
            managementDeleteSignalId: null,
        }),
    )
    .handleAction(
        ac.setPreselectedAccountOverviewSignalType,
        (state, { payload }): SignalsState => ({
            ...state,
            preselectedAccountOverviewSignalType: payload,
        }),
    )
    .handleAction(
        ac.setInitialSelectedSignalType,
        (state, { payload }): SignalsState => ({
            ...state,
            initialSelectedSignalType: payload,
        }),
    )
    .handleAction(
        ac.setManagementEditSignalId,
        (state, { payload }): SignalsState =>
            payload === null
                ? {
                      ...state,
                      managementEditSignalId: null,
                  }
                : {
                      ...state,
                      managementEditSignalId: payload,
                      managementDeleteSignalId: null,
                      preselectedSignalType: null,
                      shouldAutoApplyCreatedSignal: false,
                  },
    )
    .handleAction(
        ac.setManagementDeleteSignalId,
        (state, { payload }): SignalsState =>
            payload === null
                ? {
                      ...state,
                      managementDeleteSignalId: null,
                  }
                : {
                      ...state,
                      managementDeleteSignalId: payload,
                      managementEditSignalId: null,
                      preselectedSignalType: null,
                      shouldAutoApplyCreatedSignal: false,
                  },
    )
    .handleAction(
        ac.areSignalsAvailableChangedAction,
        (state, { payload }): SignalsState => ({
            ...state,
            areSignalsAvailable: payload,
        }),
    )
    .handleAction(
        ac.resetSignalsAvailability,
        (state): SignalsState => ({
            ...state,
            areSignalsAvailable: initialSignalsState.areSignalsAvailable,
        }),
    )
    .handleAction(
        ac.setSignalsSourceAction,
        (state, { payload }): SignalsState => ({
            ...state,
            signalsSource: payload,
        }),
    )
    .handleAction(
        ac.resetSignalsSourceAction,
        (state): SignalsState => ({
            ...state,
            signalsSource: initialSignalsState.signalsSource,
            managementEditSignalId: initialSignalsState.managementEditSignalId,
            managementDeleteSignalId: initialSignalsState.managementDeleteSignalId,
        }),
    )
    .handleAction(
        ac.fetchSignalsDescriptionAsync.request,
        (state): SignalsState => ({
            ...state,
            isSignalsDescriptionBeingFetched: true,
            signalsDescriptionFetchError: initialSignalsState.signalsDescriptionFetchError,
        }),
    )
    .handleAction(
        ac.fetchSignalsDescriptionAsync.success,
        (state, { payload }): SignalsState => ({
            ...state,
            isSignalsDescriptionBeingFetched: false,
            signalsDescription: payload,
        }),
    )
    .handleAction(
        ac.fetchSignalsDescriptionAsync.failure,
        (state, { payload }): SignalsState => ({
            ...state,
            isSignalsDescriptionBeingFetched: false,
            signalsDescriptionFetchError: payload,
        }),
    )
    .handleAction(
        ac.fetchSignalsAsync.request,
        (state): SignalsState => ({
            ...state,
            areSignalsBeingFetched: true,
            signalsFetchError: initialSignalsState.signalsFetchError,
        }),
    )
    .handleAction(
        ac.fetchSignalsAsync.success,
        (state, { payload }): SignalsState => ({
            ...state,
            areSignalsBeingFetched: false,
            signalsById: payload,
        }),
    )
    .handleAction(
        ac.fetchSignalsAsync.failure,
        (state, { payload }): SignalsState => ({
            ...state,
            areSignalsBeingFetched: false,
            signalsFetchError: payload,
        }),
    )
    .handleAction(
        ac.fetchCountPerSignalIdAsync.request,
        (state): SignalsState => ({
            ...state,
            isCountPerSignalIdBeingFetched: true,
        }),
    )
    .handleAction(
        ac.fetchCountPerSignalIdAsync.success,
        (state, { payload }): SignalsState => ({
            ...state,
            countPerSignalId: payload,
            isCountPerSignalIdBeingFetched: false,
        }),
    )
    .handleAction(
        ac.fetchCountPerSignalIdAsync.failure,
        (state): SignalsState => ({
            ...state,
            isCountPerSignalIdBeingFetched: false,
        }),
    )
    .handleAction(
        ac.createSignalAsync.request,
        (state): SignalsState => ({
            ...state,
            signalCreateError: initialSignalsState.signalCreateError,
        }),
    )
    .handleAction(
        [ac.createSignalAsync.success, ac.createDraftSignalSuccess, ac.saveDraftSignalSuccess],
        (state, { payload }): SignalsState => ({
            ...state,
            signalsById: {
                ...state.signalsById,
                [payload.id]: payload,
            },
        }),
    )
    .handleAction(ac.signalsUpsertedAction, (state, { payload }): SignalsState => {
        if (payload.length === 0) {
            return state;
        }

        const signalsById = { ...state.signalsById };

        payload.forEach((signal) => {
            signalsById[signal.id] = signal;
        });

        return { ...state, signalsById };
    })
    .handleAction(
        ac.createSignalAsync.failure,
        (state, { payload }): SignalsState => ({
            ...state,
            signalCreateError: payload,
        }),
    )
    .handleAction(
        ac.draftSignalCreatedAndApplied,
        /**
         * Event action for middleware notification
         */
        (state): SignalsState => state,
    )
    .handleAction(
        ac.updateSignalAsync.request,
        (state): SignalsState => ({
            ...state,
            isSignalBeingUpdated: true,
            signalUpdateError: initialSignalsState.signalCreateError,
        }),
    )
    .handleAction(
        ac.updateSignalAsync.success,
        (state, { payload }): SignalsState => ({
            ...state,
            isSignalBeingUpdated: false,
            signalsById: {
                ...state.signalsById,
                [payload.id]: payload,
            },
        }),
    )
    .handleAction(
        ac.updateSignalAsync.failure,
        (state, { payload }): SignalsState => ({
            ...state,
            isSignalBeingUpdated: false,
            signalUpdateError: payload,
        }),
    )
    .handleAction(ac.deleteSignalsAsync.request, (state, { payload }): SignalsState => {
        const errors = { ...state.signalDeleteErrorBySignalId };

        delete errors[payload];

        return {
            ...state,
            signalsBeingDeleted: state.signalsBeingDeleted.concat(payload),
            signalDeleteErrorBySignalId: errors,
        };
    })
    .handleAction(ac.deleteSignalsAsync.success, (state, { payload }): SignalsState => {
        const signalsBeingDeleted = state.signalsBeingDeleted.filter((id) => id !== payload);
        const wasFoundInApplied = state.appliedSignalsIds.includes(payload);
        const signalsById = { ...state.signalsById };

        delete signalsById[payload];

        if (wasFoundInApplied) {
            return {
                ...state,
                signalsBeingDeleted,
                signalsById,
                appliedSignalsIds: state.appliedSignalsIds.filter((id) => id !== payload),
            };
        }

        return {
            ...state,
            signalsBeingDeleted,
            signalsById,
        };
    })
    .handleAction(
        ac.deleteSignalsAsync.failure,
        (state, { payload: { signalId, error } }): SignalsState => ({
            ...state,
            signalsBeingDeleted: state.signalsBeingDeleted.filter((id) => id !== signalId),
            signalDeleteErrorBySignalId: {
                ...state.signalDeleteErrorBySignalId,
                [signalId]: error as string,
            },
        }),
    )
    .handleAction(ac.removeExpiredDraftSignalsAction, (state, { payload: { expiredSignalIds } }): SignalsState => {
        const signalsById = { ...state.signalsById };

        expiredSignalIds.forEach((id) => {
            delete signalsById[id];
        });

        const appliedSignalsIds = state.appliedSignalsIds.filter((id) => !expiredSignalIds.includes(id));

        return {
            ...state,
            signalsById,
            appliedSignalsIds,
        };
    })
    .handleAction(
        ac.setSignalIdForHighlight,
        (state, { payload }): SignalsState => ({
            ...state,
            signalIdForHighlight: payload,
        }),
    )
    .handleAction(
        resetFilteringStateAction,
        (state): SignalsState => ({
            ...state,
            appliedSignalsIds: initialSignalsState.appliedSignalsIds,
        }),
    );

export default signalsReducer;
